import { CellProps, Row } from 'react-table';

// --------------- Enums & Constants ---------------
export enum ParticipantStatus {
  UNKNOWN = 'Unknown / Pending',
  SCREENING_ELIGIBILITY = 'Screening (Awaiting Images/Eligibility)',
  SCREENING_AWAIT_TRAINING_TEST = 'Screened (Awaiting Training Test Data)',
  SCREENING_AWAIT_LOCALIZER_TEST = 'Trained (Awaiting Localizer Test Data)',
  SCREENING_AWAIT_SV2 = 'Screened (Awaiting SV2 VVP)',
  AWAITING_LAYOUT = 'SV2 Done (Awaiting Layout)',
  AWAITING_BASELINE = 'Layout Ready (Awaiting Baseline)',
  ACTIVE_HOME = 'Active (Home Testing)',
}

export const statusColors: Record<ParticipantStatus, string> = {
  [ParticipantStatus.UNKNOWN]: 'gray',
  [ParticipantStatus.SCREENING_ELIGIBILITY]: 'gray',
  [ParticipantStatus.SCREENING_AWAIT_SV2]: 'cyan',
  [ParticipantStatus.SCREENING_AWAIT_TRAINING_TEST]: 'cyan',
  [ParticipantStatus.SCREENING_AWAIT_LOCALIZER_TEST]: 'blue',
  [ParticipantStatus.AWAITING_LAYOUT]: 'orange',
  [ParticipantStatus.AWAITING_BASELINE]: 'purple',
  [ParticipantStatus.ACTIVE_HOME]: 'green',
};

export const testColors = {
  training: "bg-[#228B22]",
  localizer: "bg-[#4169E1]",
  home: "bg-[#4B0082]",
};

// --------------- Stakeholders & Actions ---------------
export type Stakeholder = 
  | 'REGN' 
  | 'CRC' 
  | 'Site' 
  | 'PV' 
  | 'VV' 
  | 'VV/REGN' // For data integrity/system issues
  | 'PV/Site' // For device issues requiring patient contact
  | 'VV/Site' // For adherence monitoring
  | 'VV/CRC' // For processing images and designing training and localizer layouts
  | 'N/A'    // When no specific action/stakeholder
  | 'Screen Failed';

export interface ActionableInfo {
  nextAction: string;
  responsible: Stakeholder;
}


// --------------- Data Structures ---------------
export interface RetinalImageCounts {
  [eye: string]: { // "OD" or "OS"
    [type: string]: number; // "FAF", "OCT", etc.
  }
}

export type DeviceMap = Record<string, ClinicDevice[]>;
export type LayoutCountMap = Record<string, LayoutCountData>;

/** Raw schedule-anchor value for a participant: the study-neutral anchor_date
 * (DFI Enrollment/D1 from the long-format SPT) wins; SIENNA falls back to the
 * SPT 'Rand Date' column. Feed the result to parseApiDate. */
export const getParticipantAnchorValue = (
  participant: CombinedApiData
): number | null => participant.anchor_date ?? participant['Rand Date'];

export interface CombinedApiData {
  'Site Number': string | null;
  Investigator: string | null;
  Subject: string | null;
  'Rand Date': number | null;
  /** Study-neutral schedule anchor (unix ts, ET midnight). Emitted for
   * long-format SPT studies (DFI18231: the Enrollment/D1 visit date);
   * absent for SIENNA, which anchors on 'Rand Date'. */
  anchor_date?: number | null;
  'Screening Date': number | string | null;
  'Study Eye': string | null;
  Status: string | null;
  'Screen Failed': "YES" | "NO" | undefined;
  'Overrides Applied': string | null;
  'Projected Visit 2 First Day': number | string | null;
  'Projected Visit 2 Last Day': number | string | null;
  'Projected Baseline First Day': number | string | null;
  'Projected Baseline Last Day': number | string | null;
  'Comments': string | null;
  patient_id: string | null;
  patient_user_name: string | null;
  doctor_id: string | null;
  clinic_name: string | null;
  vvp_results: VvpResult[] | null;
  total_vvp_result_count: number | null;
  training_result_count: number | null;
  localizer_result_count: number | null;
  home_result_count: number | null;
  training_test_unix_times: number[] | null;
  localizer_test_unix_times: number[] | null;
  home_test_unix_times: number[] | null;
  actual_sv2_date: number | null;
  actual_sv3_date: number | null;
  retinal_image_counts: RetinalImageCounts | null;
}

export interface ClinicDevice {
  patient_id: string;
  patient_user_name: string | null;
  device_id: string | null;
  device_model: string | null;
  device_serial_number: string | null;
  device_alias: string | null;
  allow_profile_switching: string | null;
  notes: string | null;
  clinic_device_timestamp: string | null;
  clinic_device_id: string | null;
  platform: string | null;
  home_device_is_active: string | null;
  access_info_json: {
    ip_address: string | null;
    forwarded_ip_address: string | null;
    home_activation_code: string | null;
    version: string | null;
    tags: string[] | null;
    package: string | null;
    os: string | null;
    hw_model: string | null;
    hw_serial: string | null;
    timestamp: number | null;
  } | null;
  home_device_time_requested: string | null;
  home_device_time_activated: string | null;
  home_device_time_last_login: string | null;
  home_device_version_last_login: string | null;
  clinic_name: string | null;
}

export interface VvpResult {
  bundle_label: string;
  bundle_number: string; // "0" for non-bundled, "1", "2", etc.
  bundle_period_days: string;
  bundle_layout: string;
  test_number: string;
  bundle_unix_time: string;
  test_unix_time: string;
  clinic_user_id: string;
  is_bundled: string; // "0" or "1"
  is_archived: string;
  od_ms: string;
  os_ms: string;
  od_psd: string;
  os_psd: string;
  data_version: string;
  report_branch: string;
  report_version: string;
  auto_flagged_as_outlier: string;
  override_toggle_outlier: string;
  test_id: string;
}

export interface Layout {
  name: string;
  timestamp: string;
  last_modified: string;
}

export interface LayoutCountData {
  patient_id: string;
  patient_user_name: string;
  clinic_name: string;
  layouts_json: Layout[];
}

// --------------- Cell Props ---------------
export type ParticipantCellProps<TValue = any> = CellProps<CombinedApiData, TValue>;

// Specific props for cell renderers if they need more than what ParticipantCellProps provides
export interface DeviceCellProps {
  row: Row<CombinedApiData>;
  deviceMap: DeviceMap;
}

export interface LayoutCountCellProps {
  row: Row<CombinedApiData>;
  layoutMap: LayoutCountMap;
}

export interface LastLoginCellProps {
  row: Row<CombinedApiData>;
  deviceMap: DeviceMap;
}

export interface StatusCellProps {
  row: Row<CombinedApiData>;
  layoutMap: LayoutCountMap;
  getStatusFn: (rowData: CombinedApiData, layoutData: LayoutCountData | undefined) => ParticipantStatus;
}

export interface TestIndicatorCellProps {
  row: Row<CombinedApiData>;
  layoutMap: LayoutCountMap;
  timestamps: number[] | null | undefined;
  type: 'training' | 'localizer' | 'home';
}

// Add props for cells that will need access to multiple data sources to determine action
export interface ActionableCellProps {
  row: Row<CombinedApiData>;
  layoutMap: LayoutCountMap;
  deviceMap: DeviceMap;
  // We'll pass the getActionableInfoFn similar to how getStatusFn is passed
  getActionableInfoFn: (
    rowData: CombinedApiData | undefined,
    layoutData: LayoutCountData | undefined,
    devicesForParticipant: ClinicDevice[] // Pass only relevant devices
  ) => ActionableInfo;
}


// Props for the cell
export interface AlertsCellProps {
  row: Row<CombinedApiData>;
  layoutMap: LayoutCountMap;
  deviceMap: DeviceMap;
  // Function to get alerts for a participant
  getAlertsFn: (
    rowData: CombinedApiData | undefined,
    layoutData: LayoutCountData | undefined,
    devicesForParticipant: ClinicDevice[]
  ) => ParticipantAlert[];
}

export enum AlertType {
  OVERDUE_VISIT_SV2 = 'Overdue SV2',
  OVERDUE_VISIT_SV3 = 'Overdue SV3',
  DEVICE_OFFLINE_STALE = 'Device Offline (Stale Login)',
  NO_HOME_LAYOUT_POST_SV2 = 'Home Layout Pending (Post-SV2)',
  LOW_ADHERENCE_HOME = 'Low Home Test Adherence', // Needs data source
  DEVICE_NOT_ASSIGNED_CRITICAL = 'Device Not Assigned (Critical)', // e.g. for active patient
  PROFILE_NOT_CREATED = 'VVP Profile Not Created',
}

export interface ParticipantAlert {
  type: AlertType;
  message: string; // Optional: more details for tooltip
  severity: 'error' | 'warning' | 'info';
}


// --------------- Column IDs (Optional, but good for consistency) ---------------
export const COLUMN_IDS = {
  PARTICIPANT_STATUS: 'participantStatus',
  NEXT_ACTION: 'nextAction',
  RESPONSIBLE_STAKEHOLDER: 'responsibleStakeholder',
  INVESTIGATOR: 'Investigator', // Matches accessor
  PARTICIPANT_IDENTIFIER: 'participantIdentifier',
  DEVICES: 'devicesColumn',
  LAYOUTS: 'layoutsColumn',
  LAST_LOGIN: 'lastLoginColumn',
  SITE: 'clinic_name', // Matches accessor
  SCREENING_DATE: 'screeningDate',
  SV2_STATUS: 'sv2Status',
  SV3_STATUS: 'sv3Status',
  TRAINING_INDICATORS: 'trainingIndicators',
  LOCALIZER_INDICATORS: 'localizerIndicators',
  HOME_INDICATORS: 'homeIndicators',
  IMAGES: 'imagesColumn',
  STUDY_EYE: 'studyEye',
  RAND_DATE: 'randDate',
};
